import React from 'react';
import PropTypes from 'prop-types';
import css from './YfDataStatus.css';
import Icon, { IconSizes, IconTypes, IconVariants } from '../../../components/foundations/Icon';
import Tooltip from '../../../components/molecules/Tooltip';
import MetricCell from './MetricCell';
import useElapsed from '../../hooks/useElapsed';
import {
    AUDIT_METRICS,
    AUDIT_STATUS,
    formatCheckTime,
    formatElapsed,
    hasAdnetSide,
    latestLoad,
    rowGroup,
    SLOW_PULL_MS,
    yfTranslationKey,
} from '../../yfDataStatusUtils';
import { auditRowShape } from './propTypes';

function ConnectorMark({ connector }) {
    if (connector.logo_path) {
        return <img className={css.logo} src={connector.logo_path} alt="" />;
    }
    return <span className={css.logoStub}>{(connector.display_name || '?').charAt(0)}</span>;
}

ConnectorMark.propTypes = {
    connector: auditRowShape.isRequired,
};

function LoadedBy({ pullTag, loadedAt, translate }) {
    return (
        <>
            {pullTag ? (
                <span className={css.loadTooltipTag}>{pullTag}</span>
            ) : (
                translate(yfTranslationKey('LOADED_BY_MANUAL'))
            )}
            {loadedAt &&
                translate(yfTranslationKey('LOADED_AT_TOOLTIP'), { loadedAt: formatCheckTime(loadedAt, 'UTC') })}
        </>
    );
}

LoadedBy.propTypes = {
    pullTag: PropTypes.string,
    loadedAt: PropTypes.string,
    translate: PropTypes.func.isRequired,
};

function LoadTooltip({ connector, translate }) {
    const accountLoads = (connector.loads || []).filter(load => load.account);

    if (accountLoads.length) {
        return (
            <>
                <div>{translate(yfTranslationKey('LOADED_BY_ACCOUNT_TOOLTIP'))}</div>
                <ul className={css.loadTooltipAccounts}>
                    {accountLoads.map((load, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <li key={index}>
                            <strong>{load.account}</strong>:{' '}
                            {load.pull_tag || load.loaded_at ? (
                                <LoadedBy pullTag={load.pull_tag} loadedAt={load.loaded_at} translate={translate} />
                            ) : (
                                translate(yfTranslationKey('ACCOUNT_NOT_LOADED'))
                            )}
                        </li>
                    ))}
                </ul>
            </>
        );
    }

    const load = latestLoad(connector);
    if (!load.pull_tag) {
        return translate(yfTranslationKey('LOADED_BY_MANUAL_TOOLTIP'));
    }

    return (
        <>
            {translate(yfTranslationKey('LOADED_BY_TOOLTIP'))}{' '}
            <LoadedBy pullTag={load.pull_tag} loadedAt={load.loaded_at} translate={translate} />
        </>
    );
}

LoadTooltip.propTypes = {
    connector: auditRowShape.isRequired,
    translate: PropTypes.func.isRequired,
};

function ConnectorRow({
    connector,
    isCalculating,
    isQueued = false,
    startedAt = null,
    error = null,
    onCalculate,
    translate,
}) {
    const elapsedMs = useElapsed(isCalculating ? startedAt : null);

    const group = rowGroup(connector);
    const isBusy = isCalculating || isQueued;
    const hasError = !!error && !isCalculating;
    const comparable = hasAdnetSide(connector.deltas);
    const lastLoad = latestLoad(connector);
    const isVerifiedEmpty = !comparable && connector.status === AUDIT_STATUS.SYNCED;
    const isUnavailable = connector.status === AUDIT_STATUS.UNAVAILABLE;

    return (
        <div className={`${css.row} ${css[`edge_${group}`]} ${isBusy ? css.rowBusy : ''}`}>
            <div className={css.nameCell}>
                <ConnectorMark connector={connector} />
                <div className={css.nameText}>
                    <div className={css.connectorName} title={connector.display_name}>
                        {connector.display_name}
                    </div>
                    <div className={css.subLabel} title={connector.label}>
                        {connector.label}
                    </div>
                    {connector.audit_uan_id && (
                        <div className={css.subLabel}>
                            {translate(yfTranslationKey('AUDIT_UAN'), { id: connector.audit_uan_id })}
                        </div>
                    )}
                </div>
            </div>

            {hasError && (
                <div className={css.spanningCell} title={error}>
                    <Icon
                        name="alert-triangle"
                        type={IconTypes.TABLER}
                        size={IconSizes.XX_SMALL}
                        variant={IconVariants.ERROR}
                        interactive={false}
                    />
                    <span className={`${css.spanningText} ${css.spanningError}`}>{error}</span>
                </div>
            )}

            {!hasError && isUnavailable && (
                <div className={css.spanningCell}>
                    <Icon
                        name="alert-triangle"
                        type={IconTypes.TABLER}
                        size={IconSizes.XX_SMALL}
                        variant={IconVariants.WARNING}
                        interactive={false}
                    />
                    <span className={`${css.spanningText} ${css.spanningWarning}`}>
                        {translate(yfTranslationKey('UNAVAILABLE_EXPLANATION'))}
                    </span>
                </div>
            )}

            {!hasError && isVerifiedEmpty && (
                <div className={css.spanningCell}>
                    <span className={css.spanningText}>{translate(yfTranslationKey('EMPTY_DAY_EXPLANATION'))}</span>
                </div>
            )}

            {!hasError &&
                !isVerifiedEmpty &&
                !isUnavailable &&
                AUDIT_METRICS.map(metric => (
                    <MetricCell
                        key={metric}
                        delta={connector.deltas[metric]}
                        comparable={comparable}
                        translate={translate}
                    />
                ))}

            <div className={css.loadCell}>
                <Tooltip
                    title={<LoadTooltip connector={connector} translate={translate} />}
                    wrapperClass={css.loadTooltip}
                >
                    <span className={lastLoad.loaded_at ? css.loadTag : css.loadManual}>
                        {formatCheckTime(lastLoad.loaded_at) || translate(yfTranslationKey('LOADED_BY_MANUAL'))}
                    </span>
                </Tooltip>
            </div>

            <div className={css.checkCell}>
                {isBusy ? (
                    <span className={css.checking}>
                        <span className={css.checkingSpinner} />
                        {isCalculating ? formatElapsed(elapsedMs) : translate(yfTranslationKey('STATUSES.QUEUED'))}
                    </span>
                ) : (
                    <>
                        <span className={css.checkTime}>{formatCheckTime(connector.pulled_at)}</span>
                        {connector.can_calculate && (
                            <button
                                type="button"
                                className={css.checkButton}
                                title={translate(yfTranslationKey('CALCULATE_ROW_TOOLTIP'))}
                                aria-label={translate(yfTranslationKey('CALCULATE_ROW_BUTTON'))}
                                onClick={() => onCalculate(connector.uan_id, true)}
                            >
                                <Icon
                                    name="refresh"
                                    type={IconTypes.TABLER}
                                    size={IconSizes.XX_SMALL}
                                    variant={IconVariants.ACTIVE}
                                />
                            </button>
                        )}
                    </>
                )}
            </div>

            {elapsedMs > SLOW_PULL_MS && (
                <div className={css.rowHint}>{translate(yfTranslationKey('SLOW_PULL_HINT'))}</div>
            )}
        </div>
    );
}

ConnectorRow.propTypes = {
    connector: auditRowShape.isRequired,
    isCalculating: PropTypes.bool.isRequired,
    isQueued: PropTypes.bool,
    startedAt: PropTypes.number,
    error: PropTypes.string,
    onCalculate: PropTypes.func.isRequired,
    translate: PropTypes.func.isRequired,
};

export default ConnectorRow;
